import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import NewNavbar from "../components/NewNavbar";
import { usePostPasswordResetConfirmByUidb64AndTokenMutation } from "../store/api.ts";
import { getFirstError } from "../utils.js";
import Footer from "../components/Footer.js";

const passwordRules = [
  {
    key: "length",
    label: "At least 8 characters",
    test: (value) => value.length >= 8,
  },
  {
    key: "uppercase",
    label: "At least 1 uppercase letter",
    test: (value) => /[A-Z]/.test(value),
  },
  {
    key: "lowercase",
    label: "At least 1 lowercase letter",
    test: (value) => /[a-z]/.test(value),
  },
  {
    key: "number",
    label: "At least 1 number",
    test: (value) => /\d/.test(value),
  },
  {
    key: "special",
    label: "At least 1 special character",
    test: (value) => /[^A-Za-z0-9]/.test(value),
  },
];

const ResetPassword = () => {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [touched, setTouched] = useState({
    password: false,
    confirmPassword: false,
  });

  const [resetPassword, { isLoading }] =
    usePostPasswordResetConfirmByUidb64AndTokenMutation();

  const passwordValidation = useMemo(() => {
    return passwordRules.map((rule) => ({
      ...rule,
      isValid: rule.test(formData.password),
    }));
  }, [formData.password]);

  const isPasswordValid = passwordValidation.every((rule) => rule.isValid);

  const doPasswordsMatch =
    formData.confirmPassword.length > 0 &&
    formData.password === formData.confirmPassword;

  const shouldShowPasswordRules =
    touched.password || formData.password.length > 0;

  const shouldShowConfirmError =
    touched.confirmPassword &&
    formData.confirmPassword.length > 0 &&
    !doPasswordsMatch;

  const isFormValid =
    isPasswordValid &&
    doPasswordsMatch &&
    formData.password.length > 0 &&
    formData.confirmPassword.length > 0;

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBlur = (e) => {
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));
  };

  const getPasswordValidationMessage = () => {
    const failedRule = passwordValidation.find((rule) => !rule.isValid);

    if (!failedRule) return "";

    return failedRule.label;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTouched({
      password: true,
      confirmPassword: true,
    });

    if (!isPasswordValid) {
      toast.error(`Password must include: ${getPasswordValidationMessage()}`);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      await resetPassword({
        uidb64,
        token,
        passwordResetConfirm: { password: formData.password },
      }).unwrap();
      toast.success("Password successfully reset");
      navigate("/");
    } catch (err) {
      toast.error(getFirstError(err?.data || "Something went wrong"));
    }
  };

  return (
    <>
      <div className="min-h-screen bg-[#F7F7F7] text-[#111111]">
        <NewNavbar />

        <main className="mx-auto flex min-h-[calc(100vh-110px)] w-full max-w-[1540px] items-center justify-center px-4 py-10 md:px-6">
          <section className="w-full max-w-[560px] rounded-[6px] border border-[#DCDCDC] bg-white px-5 py-8 shadow-[0_1px_2px_rgba(0,0,0,0.03)] sm:px-8 sm:py-10">
            <div className="mb-8 border-b border-[#D9D9D9] pb-7">
              <h1 className="mt-4 font-inter text-[38px] font-semibold leading-[100%] tracking-[-0.04em] text-black sm:text-[48px]">
                Set New Password
              </h1>

              <p className="mt-5 max-w-[420px] font-inter text-[14px] leading-[165%] text-[#555555] sm:text-[15px]">
                Create a new password for your Eveness account. Make sure both
                password fields match before submitting.
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block font-spaceMono text-[10px] uppercase tracking-[0.14em] text-[#777777]"
                >
                  New Password
                </label>

                <input
                  id="password"
                  type="password"
                  name="password"
                  placeholder="Enter new password"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`
                    h-[44px] w-full rounded-[5px] border
                    bg-white px-4 font-inter text-[14px] text-[#111111]
                    outline-none transition
                    placeholder:text-[#9A9A9A]
                    focus:border-[#111111]
                    ${
                      touched.password && !isPasswordValid
                        ? "border-red-400"
                        : "border-[#D9D9D9]"
                    }
                  `}
                  required
                />

                {shouldShowPasswordRules ? (
                  <div className="mt-3 rounded-[5px] border border-[#E1E1E1] bg-[#FAFAFA] px-4 py-3">
                    <p className="mb-2 font-spaceMono text-[10px] uppercase tracking-[0.14em] text-[#777777]">
                      Password Requirements
                    </p>

                    <ul className="space-y-1.5">
                      {passwordValidation.map((rule) => (
                        <li
                          key={rule.key}
                          className={`flex items-center gap-2 font-inter text-[13px] leading-[150%] ${
                            rule.isValid ? "text-[#277A3E]" : "text-[#777777]"
                          }`}
                        >
                          <span
                            className={`flex h-[16px] w-[16px] items-center justify-center rounded-full border text-[10px] ${
                              rule.isValid
                                ? "border-[#277A3E] bg-[#EAF6EE] text-[#277A3E]"
                                : "border-[#CFCFCF] bg-white text-[#999999]"
                            }`}
                          >
                            {rule.isValid ? "✓" : "•"}
                          </span>

                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="mb-2 block font-spaceMono text-[10px] uppercase tracking-[0.14em] text-[#777777]"
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`
                    h-[44px] w-full rounded-[5px] border
                    bg-white px-4 font-inter text-[14px] text-[#111111]
                    outline-none transition
                    placeholder:text-[#9A9A9A]
                    focus:border-[#111111]
                    ${
                      shouldShowConfirmError
                        ? "border-red-400"
                        : "border-[#D9D9D9]"
                    }
                  `}
                  required
                />

                {shouldShowConfirmError ? (
                  <p className="mt-2 font-inter text-[13px] leading-[150%] text-red-600">
                    Passwords do not match.
                  </p>
                ) : null}

                {doPasswordsMatch ? (
                  <p className="mt-2 font-inter text-[13px] leading-[150%] text-[#277A3E]">
                    Passwords match.
                  </p>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={isLoading || !isFormValid}
                className="
                  mt-2 inline-flex h-[44px] w-full items-center justify-center
                  rounded-[5px] bg-[#313131] px-5 font-inter text-[13px]
                  font-medium text-white transition
                  hover:bg-black
                  disabled:cursor-not-allowed disabled:opacity-60
                "
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </form>
          </section>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default ResetPassword;
